import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Label } from "recharts";
import UseGetDashBoardWithParams from "../../../../../Components/Hooks/useDashboardDataParams";
import capitalizeFirstLetter from "../../../../../Components/Utils/Funcs";
import { useNavigate } from "react-router-dom";

const RequestFromCustomer = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const navigate = useNavigate();

  const [params, setParams] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { data, loading } = UseGetDashBoardWithParams(`/new-dashboard/ypr-received`, params);

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setParams({ ...params, month: newMonth });
    setSelectedMonth(newMonth);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setParams({ ...params, year: newYear });
    setSelectedYear(newYear);
  };

  const months = [
    { name: "Jan", value: 1 },
    { name: "Feb", value: 2 },
    { name: "Mar", value: 3 },
    { name: "Apr", value: 4 },
    { name: "May", value: 5 },
    { name: "Jun", value: 6 },
    { name: "Jul", value: 7 },
    { name: "Aug", value: 8 },
    { name: "Sep", value: 9 },
    { name: "Oct", value: 10 },
    { name: "Nov", value: 11 },
    { name: "Dec", value: 12 },
  ];

  const years = Array.from(new Array(10), (val, index) => currentYear - 5 + index);

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (data?.salesmen) {
      const aggregatedData = data?.salesmen?.reduce((acc, obj) => {
        for (const [key, value] of Object.entries(obj)) {
          if (key.startsWith("Category")) {
            acc[key] = (acc[key] || 0) + value;
          }
        }
        return acc;
      }, {});

      const result = Object.entries(aggregatedData)
        .filter(([_, value]) => value > 0)
        .map(([key, value]) => ({
          name: key,
          value: value,
          label: key.charAt(key.length - 1),
        }));

      setChartData(result);
    }
  }, [data]);

  const COLORS = ["#3B82F6", "#EC4899", "#F59E0B"];

  const handleSliceClick = (label) => {
    const path = "/procurement/purchase-requests";
    navigate(path, { state: { category: label } });
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_4px_25px_rgba(0,0,0,0.04)] transition-all duration-300 h-full flex flex-col justify-between">
      <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">YPR Received Breakdown</h3>
        </div>

        <div className="flex space-x-1.5">
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 outline-none"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 text-xs font-medium text-gray-700 outline-none"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="h-52 flex items-center justify-center animate-pulse">
          <div className="h-36 bg-gray-100 rounded-xl w-full"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center flex-1">
          {/* Donut Chart */}
          <div className="md:col-span-5 relative flex justify-center items-center h-44">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={38}
                    outerRadius={58}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleSliceClick(e.label)}
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <Label
                      value={`${data?.customers?.total || "0"}`}
                      position="center"
                      style={{ fontSize: "15px", fontWeight: "bold", fill: "#1F2937" }}
                    />
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1F2937",
                      borderRadius: "8px",
                      color: "#FFF",
                      border: "none",
                      fontSize: "11px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-400 text-xs font-medium">No YPR data</div>
            )}
          </div>

          {/* Table List */}
          <div className="md:col-span-7 max-h-48 overflow-y-auto pr-1">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
                <tr>
                  <th className="py-1.5 px-2 text-left">Salesman</th>
                  <th className="py-1.5 px-1 text-center">Cat A</th>
                  <th className="py-1.5 px-1 text-center">Cat B</th>
                  <th className="py-1.5 px-1 text-center">Cat C</th>
                  <th className="py-1.5 px-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.salesmen?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors">
                    <td className="py-2 px-2 font-medium text-gray-800 truncate max-w-[90px]">
                      {capitalizeFirstLetter(item.name || "N/A")}
                    </td>
                    <td className="py-2 px-1 text-center text-blue-600 font-semibold">{item["Category A"] || 0}</td>
                    <td className="py-2 px-1 text-center text-pink-600 font-semibold">{item["Category B"] || 0}</td>
                    <td className="py-2 px-1 text-center text-amber-600 font-semibold">{item["Category C"] || 0}</td>
                    <td className="py-2 px-2 text-right font-bold text-gray-900">{item?.total || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestFromCustomer;
